/**
 * Servicio para gestión de condiciones médicas
 * Conectado al backend de agthealth-patients
 */

import axios from 'axios';
import { getAuth } from 'firebase/auth';

// Configuración base para las peticiones al API según el entorno
const API_BASE = process.env.NODE_ENV === 'development'
  ? 'http://localhost:5004'
  : (process.env.REACT_APP_API_HEALTH_PATIENT_URL);

// Prefijo de API según entorno
const API_PREFIX = process.env.NODE_ENV === 'development' ? '/api' : '';

// URL completa
const API_URL = `${API_BASE}${API_PREFIX}`;

// Configurar axios
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true,
  timeout: 30000
});

// Interceptor para añadir el token de autenticación de Firebase
apiClient.interceptors.request.use(
  async (config) => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (user) {
        const token = await user.getIdToken();
        config.headers['Authorization'] = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Error al obtener token de Firebase:', error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ===========================================
// SERVICIOS DEL CATÁLOGO DE CONDICIONES
// ===========================================

const conditionsCatalogService = {
  /**
   * Obtiene todas las condiciones del catálogo
   */
  getAll: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.type) params.append('type', filters.type);
      if (filters.category) params.append('category', filters.category);

      const queryString = params.toString();
      const url = queryString ? `/conditions-catalog?${queryString}` : '/conditions-catalog';

      const response = await apiClient.get(url);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error al obtener catálogo de condiciones:', error);
      return {
        success: false,
        data: [],
        error: error.response?.data?.message || error.message
      };
    }
  },

  /**
   * Busca condiciones en el catálogo
   */
  search: async (searchTerm) => {
    try {
      const response = await apiClient.get(`/conditions-catalog/search?q=${encodeURIComponent(searchTerm)}`);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error al buscar condiciones:', error);
      return {
        success: false,
        data: [],
        error: error.response?.data?.message || error.message
      };
    }
  },

  /**
   * Obtiene condiciones por tipo
   */
  getByType: async (type) => {
    try {
      const response = await apiClient.get(`/conditions-catalog/type/${type}`);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error al obtener condiciones por tipo:', error);
      return {
        success: false,
        data: [],
        error: error.response?.data?.message || error.message
      };
    }
  },

  /**
   * Obtiene condiciones por categoría
   */
  getByCategory: async (category) => {
    try {
      const response = await apiClient.get(`/conditions-catalog/category/${category}`);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error al obtener condiciones por categoría:', error);
      return {
        success: false,
        data: [],
        error: error.response?.data?.message || error.message
      };
    }
  }
};

// ===========================================
// SERVICIOS DE CONDICIONES DEL USUARIO
// ===========================================

const userConditionService = {
  /**
   * Obtiene todas las condiciones del usuario
   */
  getAll: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.type) params.append('type', filters.type);

      const queryString = params.toString();
      const url = queryString ? `/user-conditions?${queryString}` : '/user-conditions';

      const response = await apiClient.get(url);
      return {
        success: true,
        data: response.data.data.sort((a, b) => new Date(b.diagnosisDate) - new Date(a.diagnosisDate))
      };
    } catch (error) {
      console.error('Error al obtener condiciones del usuario:', error);
      return {
        success: false,
        data: [],
        error: error.response?.data?.message || error.message
      };
    }
  },

  /**
   * Obtiene una condición del usuario por ID
   */
  getById: async (id) => {
    try {
      const response = await apiClient.get(`/user-conditions/${id}`);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error al obtener condición:', error);
      return {
        success: false,
        data: null,
        error: error.response?.data?.message || error.message
      };
    }
  },

  /**
   * Crea una nueva condición para el usuario
   */
  create: async (conditionData) => {
    try {
      const response = await apiClient.post('/user-conditions', conditionData);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error al crear condición:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  },

  /**
   * Actualiza una condición del usuario
   */
  update: async (id, conditionData) => {
    try {
      const response = await apiClient.put(`/user-conditions/${id}`, conditionData);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error al actualizar condición:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  },

  /**
   * Elimina una condición del usuario
   */
  delete: async (id) => {
    try {
      await apiClient.delete(`/user-conditions/${id}`);
      return {
        success: true
      };
    } catch (error) {
      console.error('Error al eliminar condición:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  },

  /**
   * Marca una condición como recuperada
   */
  markAsRecovered: async (id) => {
    try {
      const response = await apiClient.patch(`/user-conditions/${id}/mark-recovered`);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error al marcar como recuperada:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  },

  /**
   * Marca una condición crónica como en remisión
   */
  markAsInRemission: async (id) => {
    try {
      const response = await apiClient.patch(`/user-conditions/${id}/mark-remission`);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error al marcar como en remisión:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  }
};

export { conditionsCatalogService, userConditionService };
